import type { ReadingProgress } from '@shared/types';
import { api } from './api';

/**
 * Reading position, kept for everyone.
 *
 * Anonymous readers get localStorage; signed-in readers get that *and* the
 * server, so a piece started on a phone resumes on a laptop. Both sides carry a
 * timestamp and the newer one wins — the alternative is a device that has been
 * closed for a week quietly dragging a reader backwards.
 */

const KEY = 'kaha:progress';

type Store = Record<string, ReadingProgress>;

function read(): Store {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Store;
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try {
    // Keep only the most recent 100 — a reader's whole history is not worth a
    // quota error that breaks saving entirely.
    const trimmed = Object.values(store)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, 100);
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(trimmed.map((p) => [p.postId, p]))));
  } catch {
    // A full or disabled localStorage must not break reading.
  }
}

export function localProgress(postId: string): ReadingProgress | null {
  return read()[postId] ?? null;
}

/** The later of the two, or whichever exists. */
export function reconcile(
  local: ReadingProgress | null,
  remote: ReadingProgress | null | undefined,
): ReadingProgress | null {
  if (!local) return remote ?? null;
  if (!remote) return local;
  return remote.updatedAt.localeCompare(local.updatedAt) >= 0 ? remote : local;
}

/**
 * Records a position locally, and on the server when signed in.
 *
 * The server write is best-effort: losing it costs cross-device resume, not the
 * reading position itself, which is already safe in localStorage.
 */
export function saveProgress(
  postId: string,
  page: number,
  percent: number,
  signedIn: boolean,
): void {
  const entry: ReadingProgress = {
    postId,
    page,
    percent: Math.min(1, Math.max(0, percent)),
    updatedAt: new Date().toISOString(),
  };

  const store = read();
  store[postId] = entry;
  write(store);

  if (signedIn) void api.saveProgress(postId, page, entry.percent).catch(() => undefined);
}
